import React, { useEffect, useState } from 'react'
import {
	Button,
	List,
	ListItem,
	ListItemText,
	CircularProgress,
	Box,
	Container,
	Typography,
	Divider,
	IconButton, useTheme, Popover, TextField, Alert,
	Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import { ArrowBack, Send } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useNavigate, useParams } from 'react-router-dom'
import getSupabase      from '../../util/supabase-utils'
import {doGet, doPost, doPut}  from "../../util/do-fetch"
import {fmtDtAndTime} from "../../util/prepkit-utils"
import LeftRight from "../LeftRight"
import {cloneDeep} from "lodash"
import AddLicenseModal from "./AddLicenseModal"

const ManageUser = () => {
	const navigate = useNavigate()
	const theme = useTheme()
	const { id } = useParams()
	const [user, setUser] = useState(null)
	const [licenses, setLicenses] = useState([])
	const [selectedLicense, setSelectedLicense] = useState(null)
	const [loading, setLoading] = useState(true)
	const [extending, setExtending] = useState(false)
	const [anchorEl, setAnchorEl] = useState(null)
	const [days, setDays] = useState('')
	const [modalOpen, setModalOpen] = useState(false)
	const [sendingLink, setSendingLink] = useState(false)
	const [linkResult, setLinkResult] = useState(null)   // {severity, message}

	// the row whose expiry is being edited, and the date in the picker
	const [editingLicense, setEditingLicense] = useState(null)
	const [editDate, setEditDate] = useState(null)
	const [savingExpiry, setSavingExpiry] = useState(false)
	const [expiryError, setExpiryError] = useState(null)

	useEffect(() => {
		fetchUserAndLicenses()
	}, [id])

	const fetchUserAndLicenses = async () => {
		try {
			const response = await doGet(`/admin/users/${id}`)
			const data = await response.json()
			if (data) {
				setUser(data.user || null)
				setLicenses(data.licenses || [])
			}
			setLoading(false)
		} catch (err) {
			console.error('Error fetching user:', err)
		}
	}

	const handleExtendClick = (event, license) => {
		setAnchorEl(event.currentTarget)
		setSelectedLicense(license)
	}

	const handleExtendClose = () => {
		setAnchorEl(null)
		setSelectedLicense(null)
	}

	const handleExtendLicense = async (daysToExtend) => {
		setExtending(true)
		try {
			const options = {
				body: JSON.stringify({ days: daysToExtend })
			}
			const pkid = selectedLicense.prepkit_id
			const url = `/admin/users/${id}/prepkit/${pkid}/extend`
			const response = await doPost(url, options)
			const data = await response.json()
			if (data) {
				setLicenses(prev => {
					const updated = [...prev]
					const index = updated.findIndex((l) => l.prepkit_id === pkid)
					const license = cloneDeep(updated[index])
					license.expires_at = data.expires_at
					updated[index] = license
					return updated
				})
			}
		} catch (err) {
			console.error('Error fetching user:', err)
		}
		setExtending(false)
		handleExtendClose()
	}

	/*
	 * Set an absolute expiry (192), as opposed to Extend, which adds days to whatever is there.
	 *
	 * Writes prepkit.expires_at -- the date the app actually reads for access -- and the expiry of
	 * the kit's CURRENT license. Earlier license rows are purchase history and are left alone; see
	 * setPrepKitExpiration in service/license.js.
	 */
	const openEditExpiry = (license) => {
		setExpiryError(null)
		setEditingLicense(license)
		setEditDate(license.expires_at ? dayjs(license.expires_at) : dayjs())
	}

	const closeEditExpiry = () => {
		setEditingLicense(null)
		setEditDate(null)
		setExpiryError(null)
	}

	const handleSaveExpiry = async () => {
		if (!editDate || !editDate.isValid()) {
			setExpiryError('Pick a valid date and time.')
			return
		}

		setSavingExpiry(true)
		setExpiryError(null)
		try {
			const pkid = editingLicense.prepkit_id
			const url = `/admin/users/${id}/prepkit/${pkid}/expiration`
			const response = await doPut(url, {body: JSON.stringify({expiresAt: editDate.toISOString()})})
			if (!response.ok) throw new Error(await response.text())

			/*
			 * Re-read rather than patch the row in place. A kit that has been renewed appears once
			 * per license row here, so there is no single row to patch -- and re-reading means this
			 * screen shows what is actually stored rather than a second guess at it.
			 */
			await fetchUserAndLicenses()
			closeEditExpiry()
		}
		catch (err) {
			console.error('Error setting expiration:', err)
			setExpiryError(err.message || 'Could not update the expiration date.')
		}
		finally {
			setSavingExpiry(false)
		}
	}

	/*
	 * Emails the user the same magic link they would get from the sign-in form (086). The API
	 * takes the address from the user record, so nothing here can aim it elsewhere.
	 *
	 * Confirmed first because it sends real mail to a real person.
	 */
	const handleSendMagicLink = async () => {
		// eslint-disable-next-line no-restricted-globals
		if (!confirm(`Email a sign-in link to ${user.email}?`)) return

		setSendingLink(true)
		setLinkResult(null)
		try {
			const response = await doPost(`/admin/users/${id}/send-magic-link`)
			if (!response.ok) throw new Error(await response.text())
			setLinkResult({severity: 'success', message: `Sign-in link sent to ${user.email}.`})
		}
		catch (err) {
			console.error('Error sending magic link:', err)
			setLinkResult({severity: 'error', message: err.message || 'Could not send the sign-in link.'})
		}
		finally {
			setSendingLink(false)
		}
	}

	const handleAddLicense = async (categoryId, packageId, packageOptionId) => {
		const url = `/admin/users/${id}/package/${packageId}/option/${packageOptionId}`
		await doPost(url)
		await fetchUserAndLicenses()
	}

	const openExtend = Boolean(anchorEl)

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" mt={4}>
				<CircularProgress />
			</Box>
		)
	}

	if (!user) {
		return (
			<Container maxWidth="sm">
				<Box mt={4}>
					<Typography variant="h6">User not found.</Typography>
				</Box>
			</Container>
		)
	}

	return (
		<Container maxWidth="sm">
			<Box mt={4}>
				<Box display="flex" flexDirection="row" alignItems="center">
					<IconButton onClick={() => navigate('/admin/users')}>
						<ArrowBack />
					</IconButton>
					<Typography variant="h5">Manage User</Typography>
				</Box>
				<Divider sx={{ my: 2 }} />

				<Typography variant="body1"><strong>First Name:</strong> {user.first_name}</Typography>
				<Typography variant="body1"><strong>Last Name:</strong> {user.last_name}</Typography>
				<Typography variant="body1"><strong>Email:</strong> {user.email}</Typography>
				<Typography variant="body1"><strong>Created At:</strong> {new Date(user.created_at).toLocaleString()}</Typography>

				<Button
					variant="outlined"
					size="small"
					startIcon={<Send />}
					disabled={sendingLink}
					onClick={handleSendMagicLink}
					sx={{ mt: 2 }}
				>
					{sendingLink ? 'Sending…' : 'Send magic link'}
				</Button>

				{linkResult && (
					<Alert severity={linkResult.severity} sx={{ mt: 2 }} onClose={() => setLinkResult(null)}>
						{linkResult.message}
					</Alert>
				)}

				<Divider sx={{ my: 2 }} />
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Typography variant="h6">Licenses</Typography>
					<Button variant="contained" size="small" color="primary"
					        onClick={() => setModalOpen(true) }>
						Add License
					</Button>
				</Box>
				<List sx={{border: `1px solid ${theme.palette.divider}`}}>
					{/*
					  * Keyed on prepkit_id + created_at, not prepkit_id alone: vw_licensed_prepkits is
					  * license JOIN prepkit with no current_license_id filter, so a renewed kit appears
					  * once per license row and prepkit_id is not unique here. Duplicate keys let React
					  * reuse the wrong row's state, which the per-row edit control cannot tolerate.
					  */}
					{licenses.map((license) => (
						<ListItem key={`${license.prepkit_id}-${license.created_at}`} sx={{ p: 2 }}>
							{
								/*
									"package_id": "9d31b4f3-edd8-4588-8a32-f7d0c78ae005",
									"package_name": "Indiana Real Estate Exam",
									"package_option_name": "1 Month",
									"package_price_in_cents": 1999,
									"created_at": "2025-03-02T16:36:09.132179+00:00",
									"expires_at": "2025-04-02T16:36:09.018+00:00",
									"prepkit_id": "2136f1a8-8846-4736-a436-dd0eb956eea1",
									"user_id": "6b4c2697-4f2e-4b28-8e85-7b3485293c34",
									"is_admin": false,
									"is_trial": true,
									"category_id": 1,
									"name": "Real Estate",
									"color": "#7f30ff",
									"color2": "#d855ff"
								*/
							}
							<LeftRight
								left={
									<Box>
										<Typography>
											{license.package_name} - {license.package_option_name}<br/>
										</Typography>
										<Box display={"flex"} alignItems={"center"}>
											<Box display={"flex"} alignItems={"center"} mr={1}>
												Expires: { fmtDtAndTime(license.expires_at) }
											</Box>
											<Button size="small"
											        onClick={() => openEditExpiry(license)}
											        sx={{minWidth: 0, p: 0, textTransform: 'none'}}>
												Edit
											</Button>
										</Box>
									</Box>
								}
								right={
									<>
										<Button variant="outlined"
										        size="small"
										        onClick={(event)=>handleExtendClick(event, license)}>
											Extend
										</Button>
										<Popover
											open={openExtend}
											anchorEl={anchorEl}
											onClose={handleExtendClose}
											anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
										>
											<Box p={2} display="flex" flexDirection="column" gap={1} width={200}>
												<TextField
													label="Days to Extend"
													size="small"
													value={days}
													onChange={(e) => setDays(e.target.value)}
												/>
												<Box display="flex" justifyContent="flex-end" gap={1}>
													<Button size={"small"}
													        onClick={handleExtendClose}
													        disabled={extending}
													>
														Cancel
													</Button>
													<Button size={"small"}
													        variant="contained"
													        onClick={() => handleExtendLicense(days)}
													        disabled={extending}
													>
														Apply
														{
															extending && <CircularProgress size={14} sx={{ml: 0.5}}/>
														}
													</Button>
												</Box>
											</Box>
										</Popover>
									</>
								}
							/>
						</ListItem>
					))}
					{licenses.length === 0 && (
						<Typography key={"nope"} variant="body2" color="textSecondary" mt={1}>
							No licenses found.
						</Typography>
					)}
				</List>
			</Box>
			<AddLicenseModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onAddLicense={handleAddLicense}
			/>

			{/*
			  * ONE dialog for the whole list, mounted here rather than inside the .map() -- the Extend
			  * Popover above is built once per row and shares a single anchorEl between all of them,
			  * which is a pattern worth not copying.
			  */}
			<Dialog open={Boolean(editingLicense)} onClose={closeEditExpiry} maxWidth="xs" fullWidth>
				<DialogTitle>Edit expiration</DialogTitle>
				<DialogContent>
					{editingLicense && (
						<>
							<Typography variant="body2" color="textSecondary" gutterBottom>
								{editingLicense.package_name} — {editingLicense.package_option_name}
							</Typography>
							<Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
								Currently expires {fmtDtAndTime(editingLicense.expires_at)}.
							</Typography>

							<DateTimePicker
								label="Expires"
								value={editDate}
								onChange={setEditDate}
								slotProps={{textField: {fullWidth: true}}}
							/>

							{/*
							  * Said plainly, because the effect is not obvious from where the pencil sits:
							  * a row can be a past purchase, and this always acts on the kit's live expiry.
							  */}
							<Alert severity="info" sx={{mt: 2}}>
								Sets the expiry on the PrepKit and on its current license. Earlier purchases
								on this kit are left as they are.
							</Alert>

							{expiryError && <Alert severity="error" sx={{mt: 2}}>{expiryError}</Alert>}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeEditExpiry} disabled={savingExpiry}>Cancel</Button>
					<Button variant="contained" onClick={handleSaveExpiry} disabled={savingExpiry}>
						Save
						{savingExpiry && <CircularProgress size={14} sx={{ml: 0.5}}/>}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	)
}

export default ManageUser
